import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AwardVisibility, BudgetPeriod } from '../../entities/awards';
import { GroupType } from '../../entities/groups';
import { BudgetMode, MessageMode } from './types';

export const useSchema = () => {
	const { t } = useTranslation('awards', { keyPrefix: 'form' });

	return z
		.object({
			amount: z.any(),
			budgetMode: z.nativeEnum(BudgetMode),
			budgetPerPeriod: z.any(),
			conditionsOperator: z.nativeEnum(GroupType),
			description: z.string(),
			icon: z.any(),
			message: z.string(),
			messageMode: z.nativeEnum(MessageMode),
			name: z.string().trim().min(1, t('errorNameTooShort')).max(40, t('errorNameTooLong')),
			periodType: z.nativeEnum(BudgetPeriod),
			requireApproval: z.boolean(),
			title: z.string().trim().min(1, t('errorInvalidTitle')).max(100, t('errorInvalidTitle')),
			visibility: z.nativeEnum(AwardVisibility),
			visibilityEditable: z.boolean(),
			approvalLevels: z.array(
				z.array(
					z.object({
						id: z.string(),
					})
				)
			),
			conditions: z.array(
				z.object({
					attr: z.string().optional(),
					value: z.string().optional(),
				})
			),
		})
		.refine((data) => data.conditions.length > 0 && data.conditions.every((cond) => cond.attr && cond.value), {
			message: t('errorInvalidConditions'),
			path: ['conditions'],
		})
		.refine(
			(data) =>
				data.budgetMode === BudgetMode.NoLimit ||
				(typeof data.budgetPerPeriod === 'number' && data.budgetPerPeriod > 0 && Math.trunc(data.budgetPerPeriod) === data.budgetPerPeriod),
			{
				message: t('errorInvalidBudget'),
				path: ['budgetPerPeriod'],
			}
		)
		.refine(
			(data) => data.budgetMode !== BudgetMode.FixedLimit || (typeof data.amount === 'number' && data.amount > 0 && Math.trunc(data.amount) === data.amount),
			{
				message: t('errorInvalidAmount'),
				path: ['amount'],
			}
		)
		.refine((data) => !data.requireApproval || data.approvalLevels.every((level) => level.length > 0), {
			message: t('errorInvalidApprovals'),
			path: ['approvalLevels'],
		})
		.refine((data) => data.messageMode === MessageMode.Custom || data.message.length > 0, {
			message: t('errorMessageRequired'),
			path: ['message'],
		})
		.refine((data) => data.messageMode === MessageMode.Custom || data.message.length < 1000, {
			message: t('errorMessageTooLong'),
			path: ['message'],
		})
		.refine((data) => !/@recipient/.test(data.message) && !/\+\d+/.test(data.message) && !/\+amount/.test(data.message), {
			message: t('errorMessageIncludesTokens'),
			path: ['message'],
		});
};
